import { useState, useEffect, useRef, useCallback } from "react";

import "./GlobalSearch.css";
import { searchAll } from "../../api/search";

import { useNavigate } from "react-router";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

const TYPES = ["etudiant", "professeur", "parent"] as const;
const STATUSES = ["active", "inactive"] as const;

type Type = (typeof TYPES)[number];
type Status = (typeof STATUSES)[number];

interface Filters {
  types: Type[];
  statuses: Status[];
}

function highlight(text: string, query: string) {
  if (!query.trim()) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="gs-highlight">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

// ── Icons (inline SVG) ─────────────────────────────────────────────────────
const IconSearch = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconClose = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconFilter = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const IconUser = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// ── Main Component ─────────────────────────────────────────────────────────
const GlobalSearch = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any>(null);
  const [filteredResults, setFilteredResults] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    types: [],
    statuses: [],
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const openSearch = useCallback(() => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults([]);
    setHasSearched(false);
    setShowFilters(false);
    setFilters({ types: [], statuses: [] });
  }, []);

  // Keyboard shortcut Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        open ? closeSearch() : openSearch();
      }
      if (e.key === "Escape" && open) closeSearch();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, openSearch, closeSearch]);

  const runSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    setHasSearched(true);

    const { results } = await searchAll(query);

    let allSearchResultNumber = 0;

    if (results?.activeYear) {
      allSearchResultNumber =
        allSearchResultNumber + (results.activeYear?.parents?.length ?? 0);
      allSearchResultNumber =
        allSearchResultNumber + (results.activeYear?.students?.length ?? 0);
      allSearchResultNumber =
        allSearchResultNumber + (results.activeYear?.teachers?.length ?? 0);
    }
    if (results?.othersYears?.length > 0) {
      for (const r of results?.othersYears) {
        allSearchResultNumber =
          allSearchResultNumber + (r?.parents?.length ?? 0);
        allSearchResultNumber =
          allSearchResultNumber + (r?.students?.length ?? 0);
        allSearchResultNumber =
          allSearchResultNumber + (r?.teachers?.length ?? 0);
      }
    }
    // console.log("Response: ", {
    //   ...results,
    //   totalResult: allSearchResultNumber,
    // });
    const formattedResults = {
      ...results,
      totalResult: allSearchResultNumber,
    };

    setResults(formattedResults);
    setFilteredResults(applyFilters(formattedResults, filters));
    setLoading(false);
    // if (!error && data) setResults(data as User[]);
  }, [query, filters]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") runSearch();
  };

  function toggleFilter<K extends keyof Filters>(
    key: K,
    value: Filters[K][number],
  ) {
    setFilters((prev) => {
      const arr = prev[key] as string[];
      const exists = arr.includes(value as string);
      return {
        ...prev,
        [key]: exists ? arr.filter((v) => v !== value) : [...arr, value],
      } as Filters;
    });
  }

  function applyFilters(data: any, filters: Filters) {
    if (!data) return null;

    const filterUsers = (users: any[], type: Type) => {
      return (users || []).filter((user) => {
        // ── Filter type ─────────────────────
        const matchType =
          filters.types.length === 0 || filters.types.includes(type);

        // ── Filter status ───────────────────
        const userStatus: Status = user?.statut ? "active" : "inactive";

        const matchStatus =
          filters.statuses.length === 0 ||
          filters.statuses.includes(userStatus);

        return matchType && matchStatus;
      });
    };

    const buildYear = (year: any) => ({
      ...year,
      students: filterUsers(year?.students || [], "etudiant"),
      teachers: filterUsers(year?.teachers || [], "professeur"),
      parents: filterUsers(year?.parents || [], "parent"),
    });

    const activeYear = data?.activeYear ? buildYear(data.activeYear) : null;

    const othersYears = data?.othersYears?.map((y: any) => buildYear(y)) || [];

    // ── Recalculate total ──────────────────
    let totalResult = 0;

    const countYear = (year: any) => {
      totalResult += year?.students?.length || 0;
      totalResult += year?.teachers?.length || 0;
      totalResult += year?.parents?.length || 0;
    };

    if (activeYear) countYear(activeYear);

    othersYears.forEach(countYear);

    return {
      ...data,
      activeYear,
      othersYears,
      totalResult,
    };
  }

  useEffect(() => {
    if (!results) return;

    const filtered = applyFilters(results, filters);

    setFilteredResults(filtered);
  }, [results, filters]);

  const activeFilterCount = filters.types.length + filters.statuses.length;

  return (
    <>
      {/* Trigger bar */}
      <div
        className="gs-trigger"
        onClick={openSearch}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && openSearch()}
      >
        <IconSearch />
        <span>Rechercher des utilisateurs…</span>
        <span className="gs-trigger-shortcut">⌘K</span>
      </div>

      {/* Overlay */}
      <div
        className={`gs-overlay${open ? " visible" : ""}`}
        onClick={closeSearch}
      />

      {/* Floating container */}
      <div className={`gs-container${open ? " visible" : ""}`}>
        {/* Search box */}
        <div className="gs-search-box">
          <span className="gs-search-icon">
            <IconSearch />
          </span>
          <input
            ref={inputRef}
            className="gs-input"
            placeholder="Nom, email, département…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
          />
          <button
            className={`gs-search-btn btn btn-primary${query.trim() ? " show" : ""}`}
            onClick={runSearch}
            disabled={loading}
          >
            {loading ? (
              <span className="gs-spinner" style={{ width: 14, height: 14 }} />
            ) : (
              <>
                <IconSearch /> Rechercher
              </>
            )}
          </button>
          <button
            className="gs-close-btn"
            onClick={closeSearch}
            title="Fermer (Esc)"
            type="button"
          >
            <IconClose />
          </button>
        </div>

        {/* Filter toggle + chips */}
        <div className={`gs-filters${showFilters ? " show" : ""}`}>
          <span className="gs-filter-label">
            <IconFilter /> Type
          </span>
          {TYPES.map((t) => (
            <button
              key={t}
              className={`gs-filter-chip${filters.types.includes(t) ? " active" : ""}`}
              onClick={() => toggleFilter("types", t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
          <span className="gs-filter-label">Statut</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              className={`gs-filter-chip${filters.statuses.includes(s) ? " active" : ""}`}
              onClick={() => toggleFilter("statuses", s)}
            >
              {s === "active" ? "Actif" : "Inactif"}
            </button>
          ))}
        </div>

        {/* Filter toggle button (below search box) */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0.35rem 0.25rem 0",
          }}
        >
          <button
            className={`gs-filter-chip${activeFilterCount > 0 ? " active" : ""}`}
            style={{ fontSize: "0.76rem" }}
            onClick={() => setShowFilters((v) => !v)}
          >
            <IconFilter />
            Filtres{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>

        {/* Results */}
        <div className={`gs-results${hasSearched || loading ? " show" : ""}`}>
          {loading && (
            <div className="gs-state">
              <div className="gs-spinner" />
              <p>Recherche en cours…</p>
            </div>
          )}

          {!loading && hasSearched && filteredResults?.totalResult <= 0 && (
            <div className="gs-state">
              <div className="gs-state-icon">
                <IconUser />
              </div>
              <p>Aucun utilisateur trouvé</p>
              <p style={{ fontSize: "0.77rem" }}>
                Essayez d'autres termes ou filtres
              </p>
            </div>
          )}

          {!loading && filteredResults?.totalResult >= 0 && (
            <>
              {filteredResults?.activeYear && (
                <>
                  <div className="gs-results-header">
                    <span className="gs-results-count">
                      {filteredResults?.totalResult} résultat
                      {filteredResults?.totalResult > 1 ? "s" : ""}
                    </span>
                    <button
                      className="gs-results-clear"
                      onClick={() => {
                        setResults(null);
                        setFilteredResults(null);
                        setHasSearched(false);
                      }}
                    >
                      Effacer
                    </button>
                  </div>
                  <div className="bg-dark text-center white">
                    {filteredResults?.activeYear?.label}
                  </div>
                  {filteredResults?.activeYear?.students?.map(
                    (user: any, idx: number) => {
                      return (
                        <OverlayTrigger
                          placement="auto"
                          overlay={<Tooltip>Etudiant</Tooltip>}
                        >
                          <div
                            key={`${user.id}-${idx}`}
                            className="gs-result-item"
                            onClick={() => {
                              navigate(`/student-detail/${user.id}`);
                              setOpen(false);
                            }}
                          >
                            {/*<Avvvatars
                              value={`${user.firstname?.charAt(0) || ""}${
                                user.lastname?.charAt(0) || ""
                              }`}
                              style="character" // ou "character"
                            />*/}
                            <ImageWithBasePath
                              src="assets/img/icons/student.svg"
                              alt="Folder"
                              className="mb-2"
                              height={48}
                              width={48}
                            />

                            <div className="gs-result-info">
                              <div className="gs-result-name">
                                {highlight(user?.firstname ?? "", query)}{" "}
                                {highlight(user?.lastname ?? "", query)}
                              </div>
                              <div className="gs-result-meta">
                                {highlight(user?.email ?? "", query)}
                              </div>
                            </div>
                            <span className={`gs-badge gs-badge-${user?.role}`}>
                              {user?.Level?.abbreviation}-{user?.Class?.label}
                            </span>
                            <span className={`gs-badge gs-badge-${user?.role}`}>
                              {user?.matricule}
                            </span>
                            <span
                              className={`gs-badge gs-badge-${user?.statut ? "active" : "inactive"}`}
                              style={{ marginLeft: 4 }}
                            >
                              {user?.statut ? "Actif" : "Inactif"}
                            </span>
                            <span className="gs-result-arrow">
                              <IconArrow />
                            </span>
                          </div>
                        </OverlayTrigger>
                      );
                    },
                  )}
                  {filteredResults?.activeYear?.teachers?.map(
                    (user: any, idx: number) => {
                      return (
                        <OverlayTrigger
                          placement="auto"
                          overlay={<Tooltip>Professeur</Tooltip>}
                        >
                          <div
                            key={`${user.id}-${idx}`}
                            className="gs-result-item"
                            onClick={() => {
                              navigate(`/teacher-detail/${user.id}`);
                              setOpen(false);
                            }}
                          >
                            {/*<Avvvatars
                              value={`${user.firstname?.charAt(0) || ""}${
                                user.lastname?.charAt(0) || ""
                              }`}
                              style="character" // ou "character"
                            />*/}
                            <ImageWithBasePath
                              src="assets/img/icons/teacher.svg"
                              alt="Folder"
                              className="mb-2"
                              height={48}
                              width={48}
                            />
                            <div className="gs-result-info">
                              <div className="gs-result-name">
                                {highlight(user?.firstname ?? "", query)}{" "}
                                {highlight(user?.lastname ?? "", query)}
                              </div>
                              <div className="gs-result-meta">
                                {highlight(user?.email ?? "", query)}
                              </div>
                            </div>
                            <span
                              className={`gs-badge gs-badge-${user?.speciality}`}
                            >
                              {user?.speciality}
                            </span>
                            <span
                              className={`gs-badge gs-badge-${user?.statut ? "active" : "inactive"}`}
                              style={{ marginLeft: 4 }}
                            >
                              {user?.statut ? "Actif" : "Inactif"}
                            </span>
                            <span className="gs-result-arrow">
                              <IconArrow />
                            </span>
                          </div>
                        </OverlayTrigger>
                      );
                    },
                  )}
                  {filteredResults?.activeYear?.parents?.map(
                    (user: any, idx: number) => {
                      return (
                        <OverlayTrigger
                          placement="auto"
                          overlay={<Tooltip>Parent</Tooltip>}
                        >
                          <div
                            key={`${user.id}-${idx}`}
                            className="gs-result-item"
                          >
                            <ImageWithBasePath
                              src="assets/img/icons/parent.webp"
                              alt="Folder"
                              className="mb-2"
                              height={48}
                              width={48}
                            />
                            <div className="gs-result-info">
                              <div className="gs-result-name">
                                {highlight(user?.firstname ?? "", query)}{" "}
                                {highlight(user?.lastname ?? "", query)}
                              </div>
                              <div className="gs-result-meta">
                                {highlight(user?.email ?? "", query)}
                              </div>
                            </div>
                            <span
                              className={`gs-badge gs-badge-${user?.speciality}`}
                            >
                              {user?.speciality}
                            </span>
                            <span
                              className={`gs-badge gs-badge-${user?.statut ? "active" : "inactive"}`}
                              style={{ marginLeft: 4 }}
                            >
                              {user?.statut ? "Actif" : "Inactif"}
                            </span>
                            <span className="gs-result-arrow">
                              <IconArrow />
                            </span>
                          </div>
                        </OverlayTrigger>
                      );
                    },
                  )}
                </>
              )}
              {filteredResults?.othersYears?.length > 0 && (
                <>
                  {filteredResults?.othersYears.map((r: any) => (
                    <>
                      <div className="bg-dark text-center white">
                        {r?.label}
                      </div>
                      {r?.students?.map((user: any, idx: number) => {
                        return (
                          <OverlayTrigger
                            placement="auto"
                            overlay={<Tooltip>Etudiant</Tooltip>}
                          >
                            <div
                              key={`${user.id}-${idx}`}
                              className="gs-result-item"
                              onClick={() => {
                                navigate(`/student-detail/${user.id}`);
                                setOpen(false);
                              }}
                            >
                              {/*<Avvvatars
                                value={`${user.firstname?.charAt(0) || ""}${
                                  user.lastname?.charAt(0) || ""
                                }`}
                                style="character" // ou "character"
                              />*/}
                              <ImageWithBasePath
                                src="assets/img/icons/student.svg"
                                alt="Folder"
                                className="mb-2"
                                height={48}
                                width={48}
                              />
                              <div className="gs-result-info">
                                <div className="gs-result-name">
                                  {highlight(user?.firstname ?? "", query)}{" "}
                                  {highlight(user?.lastname ?? "", query)}
                                </div>
                                <div className="gs-result-meta">
                                  {highlight(user?.email ?? "", query)}
                                </div>
                              </div>
                              <span
                                className={`gs-badge gs-badge-${user?.role}`}
                              >
                                {user?.Level?.abbreviation}-{user?.Class?.label}
                              </span>
                              <span
                                className={`gs-badge gs-badge-${user?.role}`}
                              >
                                {user?.matricule}
                              </span>
                              <span
                                className={`gs-badge gs-badge-${user?.statut ? "active" : "inactive"}`}
                                style={{ marginLeft: 4 }}
                              >
                                {user?.statut ? "Actif" : "Inactif"}
                              </span>
                              <span className="gs-result-arrow">
                                <IconArrow />
                              </span>
                            </div>
                          </OverlayTrigger>
                        );
                      })}
                      {r?.teachers?.map((user: any, idx: number) => {
                        return (
                          <OverlayTrigger
                            placement="auto"
                            overlay={<Tooltip>Professeur</Tooltip>}
                          >
                            <div
                              key={`${user.id}-${idx}`}
                              className="gs-result-item"
                              onClick={() => {
                                navigate(`/teacher-detail/${user.id}`);
                                setOpen(false);
                              }}
                            >
                              {/*<Avvvatars
                                value={`${user.firstname?.charAt(0) || ""}${
                                  user.lastname?.charAt(0) || ""
                                }`}
                                style="character" // ou "character"
                              />*/}
                              <ImageWithBasePath
                                src="assets/img/icons/teacher.svg"
                                alt="Folder"
                                className="mb-2"
                                height={48}
                                width={48}
                              />
                              <div className="gs-result-info">
                                <div className="gs-result-name">
                                  {highlight(user?.firstname ?? "", query)}{" "}
                                  {highlight(user?.lastname ?? "", query)}
                                </div>
                                <div className="gs-result-meta">
                                  {highlight(user?.email ?? "", query)}
                                </div>
                              </div>
                              <span
                                className={`gs-badge gs-badge-${user?.speciality}`}
                              >
                                {user?.speciality}
                              </span>
                              <span
                                className={`gs-badge gs-badge-${user?.statut ? "active" : "inactive"}`}
                                style={{ marginLeft: 4 }}
                              >
                                {user?.statut ? "Actif" : "Inactif"}
                              </span>
                              <span className="gs-result-arrow">
                                <IconArrow />
                              </span>
                            </div>
                          </OverlayTrigger>
                        );
                      })}
                      {r?.parents?.map((user: any, idx: number) => {
                        return (
                          <OverlayTrigger
                            placement="auto"
                            overlay={<Tooltip>Parent</Tooltip>}
                          >
                            <div
                              key={`${user.id}-${idx}`}
                              className="gs-result-item"
                              onClick={() => {
                                // navigate(`/teacher-detail/${user.id}`);
                                // setOpen(false);
                              }}
                            >
                              {/*<Avvvatars
                                value={`${user.firstname?.charAt(0) || ""}${
                                  user.lastname?.charAt(0) || ""
                                }`}
                                style="character" // ou "character"
                              />*/}
                              <ImageWithBasePath
                                src="assets/img/icons/parent.webp"
                                alt="Folder"
                                className="mb-2"
                                height={48}
                                width={48}
                              />
                              <div className="gs-result-info">
                                <div className="gs-result-name">
                                  {highlight(user?.firstname ?? "", query)}{" "}
                                  {highlight(user?.lastname ?? "", query)}
                                </div>
                                <div className="gs-result-meta">
                                  {highlight(user?.email ?? "", query)}
                                </div>
                              </div>
                              <span
                                className={`gs-badge gs-badge-${user?.speciality}`}
                              >
                                {user?.speciality}
                              </span>
                              <span
                                className={`gs-badge gs-badge-${user?.statut ? "active" : "inactive"}`}
                                style={{ marginLeft: 4 }}
                              >
                                {user?.statut ? "Actif" : "Inactif"}
                              </span>
                              <span className="gs-result-arrow">
                                <IconArrow />
                              </span>
                            </div>
                          </OverlayTrigger>
                        );
                      })}
                    </>
                  ))}
                </>
              )}
            </>
          )}

          {!loading && filteredResults && (
            <div className="gs-esc-hint">
              <kbd>Esc</kbd> pour fermer
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default withPermission(
  GlobalSearch,
  [
    buildPermission(Resource.TEACHER, Action.READ),
    buildPermission(Resource.PARENT, Action.READ),
    buildPermission(Resource.STUDENT, Action.READ),
  ],
  "masquer",
);
